import { Color } from '@genial/design-tokens';
import {
  FinanceResponsesPortabilityStatusInfo,
  ResponsesAssetPortabilityResponse,
  ResponsesPortabilityHistoryItemResponse,
  ResponsesPortabilityItemResponse,
} from '@genial/services/dist/models';
import { Utils } from '@genial/utils';
import { v4 as uuid } from 'uuid';

import { getValidatedStatusIcon } from '../../../utils/getValidatedStatusIcon';

import {
  IOProductItem,
  SecuritiesTransferListItemNormalized,
  SecuritiesTransferDetailItemNormalized,
  TransferStatusHistoryItem,
} from './types';

const extractStatus = (
  statusInfo: FinanceResponsesPortabilityStatusInfo,
  dateTime: Date | null | undefined,
  isLastItem: boolean
): TransferStatusHistoryItem => {
  const descriptionIsUnique = statusInfo.statusDescription !== statusInfo.statusAlias;

  const validatedStatusIcon = getValidatedStatusIcon({
    ...statusInfo.statusIcon,
    backgroundColor: statusInfo.statusIcon.bgColor,
  });

  return {
    id: uuid(),
    label: statusInfo.statusAlias || '--',
    statusIcon: {
      icon: validatedStatusIcon.icon,
      color: isLastItem ? validatedStatusIcon.iconColor : Color['neutral.light'],
      size: validatedStatusIcon.iconSize,
      bgColor: isLastItem ? validatedStatusIcon.backgroundColor : Color['neutral.lightest'],
    },
    dateTime: dateTime ? Utils.Mask.DateFormat(dateTime, 'dd/MM/yyyy HH:mm:ss') : '--',
    description: (descriptionIsUnique ? statusInfo.statusDescription : '') || undefined,
    isActive: isLastItem,
  };
};

const parseStatus = (
  asset: ResponsesAssetPortabilityResponse
): Pick<IOProductItem, 'status' | 'statusIcon' | 'statusHistory'> => {
  const lastStatus = asset.statusHistory?.slice(-1)[0];

  const validatedStatusIcon = getValidatedStatusIcon({
    ...lastStatus?.statusInfo.statusIcon,
    backgroundColor: lastStatus?.statusInfo.statusIcon.bgColor,
  });

  return {
    status: lastStatus?.statusInfo.statusAlias || '--',
    statusIcon: {
      icon: validatedStatusIcon.icon,
      color: validatedStatusIcon.iconColor,
      size: validatedStatusIcon.iconSize,
      bgColor: validatedStatusIcon.backgroundColor,
    },
    statusHistory: (asset.statusHistory || []).map((status, index) =>
      extractStatus(
        status.statusInfo,
        status.referenceDate,
        index === (asset.statusHistory || []).length - 1
      )
    ),
  };
};

const toMoney = (value: number | null | undefined) => {
  return typeof value === 'number' ? Utils.Mask.Money(value) : '--';
};

const toString = (value: number | null | undefined) => {
  return typeof value === 'number' ? String(value) : '--';
};

const normalizeAssetToIOProductItem = (asset: ResponsesAssetPortabilityResponse): IOProductItem => {
  return {
    ...parseStatus(asset),
    id: String(asset.productId || asset.assetCode || asset.isin),
    name: asset.productName || asset.assetCode || asset.isin || '--',
    amount: toMoney(asset.amount),
    quantity: toString(asset.assetQuantity),
    unitPrice: toMoney(asset.assetUnitPrice),
    itemBlocked: false,
    blockedReason: '',
  };
};

const normalizeSecuritiesTransferListItem = (
  item: ResponsesPortabilityItemResponse
): SecuritiesTransferListItemNormalized => {
  const createdAt = item.createdAt;
  const validatedStatusIcon = getValidatedStatusIcon({
    ...item.statusInfo.statusIcon,
    backgroundColor: item.statusInfo.statusIcon.bgColor,
  });

  return {
    flowTypeNormalized: item.flow,
    sourceNameNormalized: item.origin.bankName || '--',
    sourceLogoUrl: item.origin.bankIconUrl || '',
    destinationNameNormalized: item.destination.bankName || '--',
    destinationLogoUrl: item.destination.bankIconUrl || '',
    requestDateNormalized: createdAt ? Utils.Mask.DateFormat(createdAt, 'dd/MM/yyyy') : '--',
    requestedByWayOfNormalized: item.source || '--',
    protocolNormalized: item.protocolNumber || '--',
    status: item.statusInfo.statusAlias || item.statusInfo.status || '--',
    statusIcon: {
      icon: validatedStatusIcon.icon,
      color: validatedStatusIcon.iconColor,
      size: validatedStatusIcon.iconSize,
      bgColor: validatedStatusIcon.backgroundColor,
    },
  };
};

const extractIOProductsFromDetail = (
  item: ResponsesPortabilityItemResponse
): Pick<
  SecuritiesTransferDetailItemNormalized,
  'fixedIncome' | 'funds' | 'equities' | 'dividends' | 'treasury'
> => {
  const fixedIncome: IOProductItem[] = [];
  const funds: IOProductItem[] = [];
  const equities: IOProductItem[] = [];
  const dividends: IOProductItem[] = [];
  const treasury: IOProductItem[] = [];

  (item.accountsList || []).forEach((rawAccount) => {
    const marketType = rawAccount.marketType;

    if (!marketType) {
      return;
    }

    const assets = (rawAccount.assets || []).map((asset) => normalizeAssetToIOProductItem(asset));

    if (marketType === 'Equity') {
      equities.push(...assets);

      const accountEarnings = (rawAccount.earnings || []).map((asset) =>
        normalizeAssetToIOProductItem(asset)
      );

      dividends.push(...accountEarnings);
      return;
    }

    if (marketType === 'FixedIncome') {
      fixedIncome.push(...assets);
      return;
    }

    if (marketType === 'Funds') {
      funds.push(...assets);
      return;
    }

    if (marketType === 'Treasury') {
      treasury.push(...assets);
    }
  });

  return { fixedIncome, funds, equities, dividends, treasury };
};

export const normalizeSecuritiesTransferDetailItem = (
  item: ResponsesPortabilityItemResponse
): SecuritiesTransferDetailItemNormalized => {
  const createdAt = item.createdAt;

  return {
    ...extractIOProductsFromDetail(item),
    ...normalizeSecuritiesTransferListItem(item),
    solicitationTypeNormalized: item.totalPortabilityIndicator ? 'Total' : 'Parcial',
    requestDateTimeNormalized: createdAt
      ? Utils.Mask.DateFormat(createdAt, 'dd/MM/yyyy HH:mm:ss')
      : '--',
    investorNameNormalized: item.investorInfo.investorName || '--',
    investorSinacorNormalized: item.investorInfo.accountSinacor || '--',
    investorCpfCnpjNormalized: item.investorInfo.documentNumber || '--',
    grossValueNormalized: Utils.Mask.Money(item.totalOutgoingGrossValue),
    statusDescriptionNormalized: item.statusInfo.statusDescription || '--',
  };
};

export const normalizeSecuritiesTransferList = (
  response: ResponsesPortabilityItemResponse[] | null | undefined
): SecuritiesTransferListItemNormalized[] => {
  return (response || []).map(normalizeSecuritiesTransferListItem);
};

export const normalizeTransferStatusHistory = (
  response: ResponsesPortabilityHistoryItemResponse[] | null | undefined
): TransferStatusHistoryItem[] => {
  if (!response) {
    return [];
  }

  return response.reduce((acc, status, index) => {
    const isLastItem = index === response.length - 1;

    if (status.fromState.status && index === 0) {
      acc.push(
        extractStatus(status.fromState, status.createdAt, isLastItem && !status.toState.status)
      );
    }
    if (status.toState.status) {
      acc.push(extractStatus(status.toState, status.createdAt, isLastItem));
    }
    return acc;
  }, [] as TransferStatusHistoryItem[]);
};
